<template>
  <div
    class="turn-info"
    :class="{ compact, docked, opponent: isOpponent }"
  >
    <div class="turn-count">{{ t('duel.turn', { n: turnCount }) }}</div>
    <div class="current-player">
      {{ label || (isOpponent ? t('duel.opponentTurn') : t('duel.yourTurn')) }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Player } from '../types'
import { useI18n } from '../i18n/t'

const props = defineProps<{
  turnCount: number
  currentPlayer: Player
  compact?: boolean
  /** Tight pill above the opponent LP plate (desktop gutter). */
  docked?: boolean
  /** Replaces "your turn" / "opponent's turn" (admin watch). */
  label?: string
}>()

const { t } = useI18n()
const isOpponent = computed(() => props.currentPlayer === Player.PLAYER_2)
</script>

<style scoped>
.turn-info {
  display: flex;
  flex-direction: row;
  align-items: baseline;
  justify-content: center;
  gap: 10px;
  padding: 6px 14px;
  border-radius: 999px;
  border: 1px solid rgba(232, 197, 71, 0.42);
  background: rgba(8, 10, 18, 0.88);
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.35);
  color: #f4f0ea;
  white-space: nowrap;
}

.turn-info.opponent {
  border-color: rgba(248, 113, 113, 0.5);
}

.turn-count {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #fde68a;
}

.turn-info.opponent .turn-count {
  color: #fecaca;
}

.current-player {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #e2e8f0;
}

.turn-info.opponent .current-player {
  color: #fecaca;
}

.turn-info.compact {
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 6px 4px;
  border-radius: 10px;
  width: 100%;
  white-space: normal;
  text-align: center;
}

.turn-info.compact .turn-count {
  font-size: 9px;
  letter-spacing: 0.1em;
}

.turn-info.compact .current-player {
  font-size: 9px;
  line-height: 1.2;
  max-width: 56px;
}

.turn-info.docked {
  gap: 6px;
  padding: 4px 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35);
}

.turn-info.docked .turn-count {
  font-size: 10px;
  letter-spacing: 0.12em;
}

.turn-info.docked .current-player {
  font-size: 11px;
}
</style>
